// api/axiosInstance.ts
import { isTokenExpired } from '@/utils/auth';
import { fileService } from '@/utils/filesService';
import { Database } from '@/utils/persistence';
import axios from 'axios';
import { handleLogout } from './axiosInstance';
export interface Response<T> {
    ok: boolean;
    message: string;
    data: T;
}

const instance = axios.create({
    baseURL: import.meta.env.VITE_PROTOMIA_URL, // 从环境变量中读取 API 基础 URL
    timeout: undefined, // 请求超时时间
    withCredentials: true
});


// 请求拦截器
instance.interceptors.request.use(
    async config => {
        // 检查是否需要跳过 token
        if (config.headers && config.headers['Skip-Auth']) {
            delete config.headers['Skip-Auth'];
            return config;
        }
        let token = localStorage.getItem('token');
        if (token) {
            if (isTokenExpired(token)) {
                // // Token expired, refresh it
                // const originalRequest = config;
                // try {
                //   const res = await refreshToken();
                //   if (res) {
                //     token = res.token;
                //   }
                //   localStorage.setItem('token', token);
                //   originalRequest.headers.Authorization = `Bearer ${token}`;
                //   return axiosInstance(originalRequest);
                // } catch (error) {
                //   console.error('Failed to refresh token', error);
                handleLogout();
                window.location.href = (import.meta.env.VITE_ROUTE_BASE ?? "") + '/login';
                // return Promise.reject(error);
                // }
            } else {
                config.headers.Authorization = `Bearer ${token}`;
            }
        } else {
            window.location.href = (import.meta.env.VITE_ROUTE_BASE ?? "") + '/login';
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);


export type ContextEntry = {
    type: 'file' | 'folder';
    name: string;
    path: string;
    isPined?: boolean;
};

export type ChatPayload = {
    messages: { role: string, content: string };
    working_directory: ContextEntry[];
    client_action_id?: string | null;
}

export type ChatResponse = {
    delivery_mode: string,
    session_id: string,
    status: string,
    stream_url: string,
    subject: string,
    thread_id: string,
}

export const chat = async (payload: ChatPayload, thread_id: string): Promise<ChatResponse | undefined> => {
    try {
        const response = await instance.post<ChatResponse>('/api/chat', payload, {
            headers: {
                'Content-Type': 'application/json',
                'Thread-ID': thread_id,
                'Prefer': 'respond-async'
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to send chat message', e);
        return undefined;
    }
}

export type SavePayload = {
    save_path: ContextEntry[];
}

export const save = async (payload: SavePayload) => {
    try {
        const response = await instance.post('/api/save', payload, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to save data', e);
        return undefined;
    }
}

export const getConversationList = async () => {
    try {
        const response = await instance.get('/api/conversations', {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to get conversation list', e);
        return undefined;
    }
}

export const getConversationMessages = async (thread_id: string) => {
    try {
        const response = await instance.get(`/api/conversations/${thread_id}`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to get conversation messages', e);
        return undefined;
    }
}

/**
 * Probe whether a thread is still actively streaming on the backend
 * (Redis-backed; dral + engineer 2026-05-15). On page reload the
 * frontend calls this with the thread_id; if ``is_active=true`` it
 * can re-open an SSE subscription to receive new events without
 * sending a follow-up message. When inactive, falls through to the
 * normal history-only render.
 *
 * Response shape:
 *   { is_active: boolean,
 *     thread_id: string,
 *     session_id?: string,
 *     stream_url?: string,
 *     working_dir?: string | null,
 *     working_directory?: any[] | null }
 *
 * Returns undefined on network failure (caller falls back to
 * history-only).
 */
export interface SubscribeThreadResponse {
    is_active: boolean;
    thread_id: string;
    session_id?: string;
    stream_url?: string;
    working_dir?: string | null;
    working_directory?: any[] | null;
}

export const subscribeThread = async (
    thread_id: string,
): Promise<SubscribeThreadResponse | undefined> => {
    try {
        const response = await instance.post(
            '/api/stream/subscribe',
            undefined,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Thread-ID': thread_id,
                },
            },
        );
        return response?.data;
    } catch (e) {
        console.error('Failed to subscribe to thread', e);
        return undefined;
    }
};

// turn-lifecycle-blueprint P1 single-Stop:
//   action 'stop'    = graceful 1st-press (pause; jobs kept; resume=type)
//   action 'discard' = escalate 2nd-press (cancel turn; cancel_jobs
//                       opt-in also scancels running cluster jobs)
// Omitting opts posts {thread_id} only → backend defaults to discard
// (back-compat with any legacy caller).
export const cancel = async (
    thread_id: string,
    opts?: { action?: 'stop' | 'discard' | 'resume' | 'steer_now'; cancel_jobs?: boolean },
) => {
    try {
        const response = await instance.post('/api/workflow/cancel', {
            thread_id,
            ...(opts?.action ? { action: opts.action } : {}),
            ...(opts?.cancel_jobs ? { cancel_jobs: true } : {}),
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to cancel the task', e);
        return undefined;
    }
}

export const reset = async (thread_id?: string) => {
    try {
        const response = await instance.post('/api/reset', { thread_id }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to reset the conversation', e);
        return undefined;
    }
}

export const renameConversation = async (thread_id: string, new_title: string) => {
    try {
        const response = await instance.put(`/api/conversations/${thread_id}/rename`, { title: new_title }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to rename the conversation', e);
        return undefined;
    }
}


export const deleteConversation = async (thread_id: string) => {
    try {
        const response = await instance.delete(`/api/conversations/${thread_id}`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to delete the conversation', e);
        return undefined;
    }
}

export const getDefaultModels = async () => {
    try {
        const response = await instance.get(`/api/defaultModels`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to get defaultModels', e);
        return undefined;
    }
}


// ── Autopilot control ─────────────────────────────────────────────────────────

export type AutopilotCommandKind =
    | 'pause'
    | 'continue'
    | 'cancel'
    | 'replan'
    | 'skip'
    | 'update_plan';

export type AutopilotTodoItem = {
    id: string;
    title: string;
    status: string; // "pending" | "running" | "completed" | "skipped"
};

export type AutopilotStateSnapshot = {
    status?: string; // "running" | "paused" | "cancelled" | "completed" | …
    todo?: AutopilotTodoItem[];
    current_step_id?: string;
    current_step_title?: string;
    completed_count?: number;
    total_count?: number;
    _status_internal?: string;
};

export type AsyncWorkChip = {
    handle_source: string;
    handle_id: string;
    sacct_status?: string;
    queue_position?: number;
    runtime_seconds?: number;
    bundle_dir?: string;
    recent_log_tail?: string;
};

export type AutopilotCommandResponse = {
    status: 'success' | 'error';
    thread_id: string;
    outcome: string;
    observation: string;
    autopilot?: AutopilotStateSnapshot;
    message?: string;
    error?: string;
};

export const autopilotCommand = async (
    thread_id: string,
    kind: AutopilotCommandKind,
    argument?: string,
): Promise<AutopilotCommandResponse | undefined> => {
    try {
        const response = await instance.post<AutopilotCommandResponse>(
            '/api/autopilot_command',
            { thread_id, kind, ...(argument !== undefined ? { argument } : {}) },
            { headers: { 'Content-Type': 'application/json' } },
        );
        return response?.data;
    } catch (e) {
        console.error('Failed to send autopilot command', e);
        return undefined;
    }
};

// ── Model Selection ──────────────────────────────────────────────────────────

export type ModelInfo = {
    name: string;
    model: string;
    context_window: number;
};

export type GetModelsResponse = {
    models: ModelInfo[];
    current_preference: string | null;
};

export const getModels = async (): Promise<GetModelsResponse | undefined> => {
    try {
        const response = await instance.get<GetModelsResponse>('/api/models', {
            headers: { 'Content-Type': 'application/json' },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to get models', e);
        return undefined;
    }
};

export type SetUserModelResponse = {
    status: string;
    model: string;
};

export const setUserModel = async (model: string): Promise<SetUserModelResponse | undefined> => {
    try {
        const response = await instance.put<SetUserModelResponse>('/api/user/model', { model }, {
            headers: { 'Content-Type': 'application/json' },
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to set user model', e);
        return undefined;
    }
};

export type SetConversationModelResponse = {
    status: string;
    thread_id: string;
    model: string;
};

export const setConversationModel = async (thread_id: string, model: string): Promise<SetConversationModelResponse | undefined> => {
    try {
        const response = await instance.put<SetConversationModelResponse>(
            `/api/conversations/${encodeURIComponent(thread_id)}/model`,
            { model },
            { headers: { 'Content-Type': 'application/json' } },
        );
        return response?.data;
    } catch (e) {
        console.error('Failed to set conversation model', e);
        return undefined;
    }
};

// ── personal API access (account_center via the gateway) ──────────────────────
// Platform-scoped keys: minted/listed/revoked on THIS platform only. The raw key is in the
// mint response exactly once. These throw on failure (unlike the swallowing helpers above)
// because the panel must tell a 403 "ask an administrator" apart from a 503 "not enabled".
export type ApiKeyRow = {
    id: number;
    prefix: string;
    label: string;
    scopes: string;
    created_at: string;
    last_used_at?: string | null;
    revoked_at?: string | null;
};

export const listApiKeys = async (): Promise<ApiKeyRow[]> => {
    const response = await instance.get<Response<ApiKeyRow[]>>('/api/account/keys');
    return response?.data?.data ?? [];
};

export type ApiEntitlements = { model: boolean; mcp: boolean };

export const getApiEntitlements = async (): Promise<ApiEntitlements> => {
    const response = await instance.get<Response<ApiEntitlements>>('/api/account/entitlements');
    return response?.data?.data ?? { model: false, mcp: false };
};

export const mintApiKey = async (label: string, scopes: string): Promise<{ key: string; data: ApiKeyRow; notice?: string }> => {
    const response = await instance.post('/api/account/keys', { label, scopes }, {
        headers: { 'Content-Type': 'application/json' },
    });
    return response?.data;
};

export const revokeApiKey = async (id: number): Promise<void> => {
    await instance.delete(`/api/account/keys/${id}`);
};
